import { QueryFunctionContext } from '@tanstack/react-query';
import { omitBy, trim } from 'lodash';
import {
	ApiFormattedResponseData,
	ApiPaginatedResponse,
	ApiQueryOptions,
	CustomFilters,
	UUID,
} from '@/types/types';
import { QueryKeys } from '@/configs/queryKeys';
import httpClient from './httpClient';

export interface BaseEntity {
	id: UUID;
	updatedAt: string;
	createdAt: string;
}

abstract class EntityApi<TData extends BaseEntity> {
	readonly queryKey: QueryKeys;

	constructor(queryKey: QueryKeys) {
		this.queryKey = queryKey;
		this.update = this.update.bind(this);
		this.create = this.create.bind(this);
		this.delete = this.delete.bind(this);
		this.getOneMutation = this.getOneMutation.bind(this);
	}

	async getAll({
		queryKey,
	}: QueryFunctionContext<
		[
			QueryKeys,
			Partial<Pick<ApiQueryOptions<TData>, 'search' | 'sortBy'>>?,
			CustomFilters?
		]
	>) {
		const params = omitBy(
			{
				search: trim(queryKey[1]?.search),
				sortBy: Object.values(queryKey[1]?.sortBy || {}).join(':'),
				limit: 0,
				...queryKey[2],
			},
			(value) => value === ''
		);

		const response = await httpClient.get<ApiPaginatedResponse<TData>>(
			queryKey[0],
			{
				params,
			}
		);

		return response.data.data;
	}

	async getPaginated({
		queryKey,
	}: QueryFunctionContext<
		[QueryKeys, ApiQueryOptions<TData>, CustomFilters?]
	>): Promise<ApiFormattedResponseData<TData>> {
		const params: Record<string, any> = {
			page: queryKey[1].page,
			limit: queryKey[1].limit,
			search: trim(queryKey[1].search),
			searchFields: queryKey[1].searchFields.join(','),
			...queryKey[2],
		};

		if (queryKey[1].sortBy) {
			const orderKey = queryKey[1].sortBy.key?.replace('_', '.');

			params[`order[${orderKey}]`] = queryKey[1].sortBy.order;
		}

		for (const key in params) {
			if (params[key] === '') delete params[key];
		}

		const response = await httpClient.get<ApiPaginatedResponse<TData>>(
			queryKey[0],
			{
				params,
			}
		);

		return {
			data: response.data.data,
			totalPages: response.data.totalPages,
		};
	}

	async fetchOne(id: UUID) {
		const reponse = await httpClient.get<TData>(`${this.queryKey}/${id}`);
		return reponse.data;
	}

	async getOne({ queryKey }: QueryFunctionContext<[QueryKeys, UUID]>) {
		const response = await httpClient.get<TData>(
			`${queryKey[0]}/${queryKey[1]}`
		);

		return response.data;
	}

	async getOneMutation(id: UUID) {
		const response = await httpClient.get<TData>(`${this.queryKey}/${id}`);

		return response.data;
	}

	async update(payload: Partial<TData>) {
		const response = await httpClient.patch<TData>(
			`${this.queryKey}/${payload.id}`,
			payload
		);

		return response.data;
	}

	async create(payload: Partial<TData>) {
		const response = await httpClient.post<TData>(this.queryKey, payload);

		return response.data;
	}

	async delete(id: UUID) {
		await httpClient.delete(`${this.queryKey}/${id}`);
	}
}

export default EntityApi;
